import { useCallback, useEffect, useRef } from "react";

import type { ResponseAPI } from "@shapr3d/frontend-lib";
import { isLoggedIn, removeTokens, SpinnerCenter } from "@shapr3d/frontend-lib";
import { useMutation } from "@tanstack/react-query";
import queryString from "query-string";
import { useLocation } from "react-router-dom";

import AppAuth from "../components/AppAuth/AppAuth";
import { queryKeys } from "../config";
import useNavigation from "../hooks/navigation";
import useAccount from "../hooks/useAccount";
import { useLogout } from "../hooks/useAuth";
import { useGetAppProperties } from "../hooks/useGetAppProperties";
import { UMSrequestClient } from "../stores/requestClient";
import { SearchParam } from "../types";
import { PageSource } from "../types/tracking";
import { getShaprURLProtocol } from "../urlUtils";

export const APP_AUTHORIZATION_EXPIRATION_IN_MS = 60 * 60 * 1000;

export const useHandleLoginRedirect = () => {
  const navigation = useNavigation();
  const location = useLocation();

  const { mutateAsync: mutateLogout } = useLogout({
    redirect: false,
    withRequest: true,
  });

  const isRedirectingRef = useRef(false);
  const handleLoginRedirect = useCallback(async () => {
    if (isRedirectingRef.current) return;
    isRedirectingRef.current = true;

    const redirectParam = {
      [SearchParam.RedirectUrl]: encodeURIComponent(window.location.href),
      [SearchParam.Source]: PageSource.AppAuth,
    };

    if (isLoggedIn()) {
      try {
        await mutateLogout();
      } catch {
        removeTokens();
      }
    }

    const queryParams = queryString.parse(location.search);
    if (queryParams[SearchParam.Signup] === "true") {
      navigation.goToSignUp({ additionalSearchParams: redirectParam });
    } else {
      navigation.goToSignIn({ additionalSearchParams: redirectParam });
    }
  }, [location, navigation, mutateLogout]);

  useEffect(() => {
    if (!isLoggedIn()) {
      handleLoginRedirect();
    }
  }, [handleLoginRedirect]);

  return handleLoginRedirect;
};

const launchApp = ({
  codeChallenge,
  code,
  id,
  source,
}: {
  code: string | undefined;
  id: string | undefined;
  source: string | string[];
  codeChallenge: string | string[] | null;
}) => {
  const queryParams = {
    [SearchParam.AuthorizationCode]: code,
    [SearchParam.AuthorizationCodeChallenge]: codeChallenge,
    [SearchParam.Source]: source,
  };
  const protocol = getShaprURLProtocol();
  const url = `${protocol}://auth/app/${id}?${queryString.stringify(queryParams)}`;
  window.location.replace(url);
};

const useMutateAuthorizeApp = () => {
  const location = useLocation();
  const originalQuery = queryString.parse(location.search);
  const codeChallenge = originalQuery[SearchParam.AuthorizationCodeChallenge];
  const source = originalQuery[SearchParam.Source] ?? PageSource.Signin;

  return useMutation({
    mutationKey: [...queryKeys.authenticatedQueryKey],
    mutationFn: (codeChallenge: string | string[] | null) => {
      return UMSrequestClient.authenticatedRequest({
        method: "POST",
        url: "/auth/app",
        body: { codeChallenge },
      }).then((res: ResponseAPI) => {
        return res.json<{ code: string; id: string }>();
      });
    },
    onSuccess: ({ code, id }) => {
      launchApp({ code, id, source, codeChallenge });
    },
  });
};

const useHandleSegmentationSurveyRedirect = () => {
  const location = useLocation();
  const originalQuery = queryString.parse(location.search);
  const navigation = useNavigation();
  const handleSegmentationSurveyRedirect = useCallback(async () => {
    const redirectURL = new URL(window.location.href);
    redirectURL.searchParams.append(SearchParam.FromSurvey, "true");
    const redirectParam = {
      [SearchParam.RedirectUrl]: encodeURIComponent(redirectURL.toString()),
    };

    navigation.goToSegmentationSurvey(redirectParam);
  }, [navigation]);
  const appProperties = useGetAppProperties();

  useEffect(() => {
    const isSurveyCompleted =
      appProperties.data?.segmentation_v2_finished_at != undefined ||
      appProperties.data?.segmentation_work_web_completed ||
      appProperties.data?.segmentation_work_completed;
    const isResegmentationNeeded = appProperties.data?.is_resegmentation_needed;
    const isSurveyEnabled = originalQuery[SearchParam.SegmentationSurvey] === "true";
    if (
      isSurveyEnabled &&
      appProperties.isSuccess &&
      (!isSurveyCompleted || isResegmentationNeeded)
    ) {
      handleSegmentationSurveyRedirect();
    }
  }, [appProperties, originalQuery, handleSegmentationSurveyRedirect]);
};

const AppAuthContainer: React.FC = () => {
  const onLoginRedirect = useHandleLoginRedirect();
  useHandleSegmentationSurveyRedirect();

  const { data, isError: isAccountError, isLoading: isAccountLoading } = useAccount();
  const navigation = useNavigation();
  const location = useLocation();
  const appProperties = useGetAppProperties();
  const isSurveyCompleted =
    (appProperties.data?.segmentation_v2_finished_at != undefined ||
      appProperties.data?.segmentation_work_web_completed ||
      appProperties.data?.segmentation_work_completed) &&
    !appProperties.data?.is_resegmentation_needed;
  const authorizeApp = useMutateAuthorizeApp();

  const originalQuery = queryString.parse(location.search);
  const isSurveyEnabled = originalQuery[SearchParam.SegmentationSurvey] === "true";
  const codeChallenge = originalQuery[SearchParam.AuthorizationCodeChallenge];

  const inapp = originalQuery[SearchParam.InApp] === "true";

  const handleLaunchApp = useCallback(() => {
    const originalQuery = queryString.parse(location.search);
    const codeChallenge = originalQuery[SearchParam.AuthorizationCodeChallenge];
    const source = originalQuery[SearchParam.Source] ?? PageSource.Signin;
    launchApp({ codeChallenge, source, code: authorizeApp.data?.code, id: authorizeApp.data?.id });
  }, [authorizeApp.data?.code, authorizeApp.data?.id, location.search]);

  let hasError = false;
  if (!codeChallenge || isAccountError) {
    hasError = true;
  }

  const handleContinueAs = useCallback(() => {
    authorizeApp.mutate(codeChallenge);
  }, [codeChallenge, authorizeApp]);

  const handleOnBackToLogin = () => {
    authorizeApp.reset();
    navigation.omitQueryParams([SearchParam.Source]);
  };

  const source = originalQuery[SearchParam.Source];
  const fromLogin =
    source === PageSource.Signin ||
    source === PageSource.Signup ||
    source === PageSource.SSOCallback;
  const fromSurvey = originalQuery[SearchParam.FromSurvey] === "true";

  useEffect(() => {
    if (authorizeApp.isIdle) {
      if ((!isSurveyEnabled && fromLogin) || (isSurveyCompleted && (fromSurvey || fromLogin))) {
        handleContinueAs();
      }
    }
  }, [isSurveyEnabled, isSurveyCompleted, authorizeApp, handleContinueAs, fromLogin, fromSurvey]);

  const fromLoginLoading = fromLogin && (authorizeApp.isIdle || authorizeApp.isPending);
  if (!hasError && (fromLoginLoading || isAccountLoading)) {
    if (inapp) {
      return null;
    }
    return <SpinnerCenter />;
  }

  if (inapp && authorizeApp.isSuccess) {
    return null;
  }
  if (appProperties.isLoading) {
    return <SpinnerCenter />;
  }

  return (
    <AppAuth
      onContinueClick={handleContinueAs}
      onLoginClick={onLoginRedirect}
      onLaunchAppClick={handleLaunchApp}
      onBackToLoginClick={handleOnBackToLogin}
      isSurveySuccess={Boolean(isSurveyCompleted) && fromSurvey}
      email={data?.email ?? ""}
      authState={authorizeApp.status}
      hasError={hasError}
      isExpired={false}
    />
  );
};

export default AppAuthContainer;
